<script>
  import { auth } from '../../state/auth.svelte.js';
  import { imageModels } from '../../state/model-catalog.svelte.js';
  import { getSogniClientInstance } from '../../sogni-client.svelte.js';
  import { getModelConfig } from '../../model-configs.js';

  let { selectedIds = [], overrides = $bindable({}), preset = 'final', readonly = false } = $props();
  let optionsById = $state({});
  let error = $state('');
  const selectedModels = $derived(imageModels.filter((model) => selectedIds.includes(model.id)));

  $effect(() => {
    const ids = selectedModels.map((model) => model.id);
    const client = getSogniClientInstance();
    if (!auth.isLoggedIn || !client || !ids.length) return;
    let current = true;
    error = '';
    Promise.all(ids.map(async (id) => [id, await client.projects.getModelOptions(id)]))
      .then((entries) => { if (current) optionsById = Object.fromEntries(entries); })
      .catch((failure) => { if (current) error = failure.message || 'Unable to load model controls.'; });
    return () => { current = false; };
  });

  function valueFor(model, field, fallback) {
    if (field === 'steps' && preset === 'draft') return overrides[model.id]?.steps ?? optionsById[model.id].steps.min;
    return overrides[model.id]?.[field] ?? getModelConfig(model.id)[field] ?? fallback;
  }

  function updateValue(modelId, field, value) {
    overrides = { ...overrides, [modelId]: { ...overrides[modelId], [field]: value } };
  }
</script>

<details class="model-parameter-controls">
  <summary>Model settings</summary>
  {#if error}<p class="control-error" role="alert">{error}</p>{/if}
  {#each selectedModels as model (model.id)}
    {@const options = optionsById[model.id]}
    {@const parameters = model.parameters || {}}
    <fieldset disabled={readonly}>
      <legend>{model.label}</legend>
      <div class="control-grid">
        {#if !model.isPartner && options}
          {#each ['steps', 'guidance'] as field}
            {#if options[field]}
              {@const range = options[field]}
              <label>{field === 'steps' ? 'Steps' : 'Guidance'}
                <input type="number" min={range.min} max={range.max} step={range.step || (range.decimals ? 10 ** -range.decimals : 1)} disabled={range.min === range.max}
                  value={valueFor(model, field, range.default)} onchange={(event) => updateValue(model.id, field, Number(event.currentTarget.value))} />
              </label>
            {/if}
          {/each}
          {#each ['sampler', 'scheduler', 'vae'] as field}
            {#if options[field]?.allowed?.length}
              <label>{field === 'vae' ? 'VAE' : field === 'sampler' ? 'Sampler' : 'Scheduler'}
                <select value={valueFor(model, field, options[field].default)} onchange={(event) => updateValue(model.id, field, event.currentTarget.value)}>
                  {#each options[field].allowed as choice}<option value={choice}>{parameters.vae?.labels?.[choice] || choice}</option>{/each}
                </select>
              </label>
            {/if}
          {/each}
        {/if}
        <label>Output format
          <select value={overrides[model.id]?.outputFormat || 'png'} onchange={(event) => updateValue(model.id, 'outputFormat', event.currentTarget.value)}>
            {#each parameters.outputFormats || ['png', 'jpeg', 'webp'] as format}<option value={format === 'jpeg' ? 'jpg' : format}>{format.toUpperCase()}</option>{/each}
          </select>
        </label>
        {#if model.isPartner}
          <label>Image quality
            <select value={overrides[model.id]?.gptImageQuality || 'medium'} onchange={(event) => updateValue(model.id, 'gptImageQuality', event.currentTarget.value)}>
              {#each (parameters.qualities || []).filter((choice) => choice !== 'auto') as quality}<option value={quality}>{quality}</option>{/each}
            </select>
          </label>
          <label>Background
            <select value={overrides[model.id]?.gptImageBackground || 'opaque'} onchange={(event) => updateValue(model.id, 'gptImageBackground', event.currentTarget.value)}>
              {#each (parameters.backgrounds || []).filter((choice) => choice !== 'auto') as background}<option value={background}>{background}</option>{/each}
            </select>
          </label>
        {/if}
      </div>
      {#if parameters.width}<p>{parameters.width.min}–{parameters.width.max}px wide · multiples of {parameters.width.step || 1}</p>{/if}
      {#if parameters.height}<p>{parameters.height.min}–{parameters.height.max}px high · multiples of {parameters.height.step || 1}</p>{/if}
    </fieldset>
  {/each}
</details>

<style>
  summary { font-size: 0.875rem; cursor: pointer; padding: 0.5rem 0; min-height: 44px; display: list-item; }
  fieldset { border: 1px solid var(--color-border); border-radius: 8px; padding: 0.6rem; margin: 0.5rem 0; min-width: 0; }
  legend { font-size: 0.75rem; padding: 0 0.3rem; }
  .control-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
  label { display: grid; gap: 0.2rem; font-size: 0.7rem; }
  input, select { width: 100%; min-width: 0; min-height: 44px; box-sizing: border-box; font: inherit; color: var(--color-text); background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 6px; padding: 0.4rem; }
  p { margin: 0.4rem 0 0; font-size: 0.7rem; color: var(--color-text-muted); }
  .control-error { color: var(--color-error); }
</style>
